import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Spinner, Typography } from "@material-tailwind/react";
import { Helmet } from "react-helmet";
import Layout from "../components/layout/Layout";
import PropertySelector from "../components/PropertySelector";
import { useProperties } from "../contexts/PropertiesContext";
import { colors } from "../utils/colors";
import PropertyCard from "../components/PropertyCard"; 
import { trackEvent } from "../utils/analytics";

export default function BankImprint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, loading, error } = useProperties();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);

  useEffect(() => {
    if (!loading && properties.length > 0) {
      let propertyToSelect = null;
      if (id) {
        // Si un ID est dans l'URL, on cherche cette propriété
        const foundProperty = properties.find(p => p.id.toString() === id);
        // On la sélectionne seulement si l'empreinte est nécessaire via actionsNeeded
        if (foundProperty && foundProperty.actionsNeeded?.bankImprintNeeded) {
          propertyToSelect = foundProperty;
        } else {
          // Si l'ID est invalide ou l'empreinte n'est pas requise pour cet ID, on redirige vers la base
          navigate("/bank-imprint", { replace: true });
          trackEvent('bankimprint_redirect', {
            fromId: id,
            reason: foundProperty ? 'imprint_not_needed' : 'property_not_found'
          });
          return; // Arrêter le hook ici
        }
      } else {
        // Si pas d'ID, on cherche la première propriété nécessitant une empreinte
        propertyToSelect = properties.find(p => p.actionsNeeded?.bankImprintNeeded);
        if (propertyToSelect) {
          // Si on en trouve une, on redirige vers son URL spécifique
          navigate(`/bank-imprint/${propertyToSelect.id}`, { replace: true });
          trackEvent('bankimprint_auto_select', {
            propertyId: propertyToSelect.id
          });
        } 
        // Si aucune propriété n'a besoin d'empreinte, propertyToSelect reste null (géré plus bas)
      }

      setSelectedProperty(propertyToSelect);
      if (propertyToSelect) {
         trackEvent('bankimprint_view_property', {
           propertyId: propertyToSelect.id
         });
      }
      // Si propertyToSelect est null après toutes les vérifications, on affichera "Aucun dossier..."
      if(!propertyToSelect){
        trackEvent('bankimprint_none_needed', {});
      }
    }
  }, [id, properties, loading, navigate]);

  // Filtrer les propriétés qui nécessitent une empreinte pour le sélecteur
  const propertiesNeedingImprint = properties.filter(p => p.actionsNeeded?.bankImprintNeeded);

  const handlePropertyChange = (newId) => {
    trackEvent('bankimprint_change_property', {
      fromPropertyId: selectedProperty ? selectedProperty.id : null,
      toPropertyId: newId
    });
    
    // Pas besoin de setSelectedProperty ici car le useEffect va le gérer via navigate
    navigate(`/bank-imprint/${newId}`, { replace: true });
    setIframeLoading(true); // Réinitialiser le chargement de l'iframe
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
    if (selectedProperty) {
      trackEvent('bankimprint_iframe_loaded', {
        propertyId: selectedProperty.id
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Empreinte bancaire - Confirmation RDV - Dimo Diagnostic</title>
        <meta name="description" content="Confirmez votre rendez-vous en enregistrant une empreinte bancaire." />
        <link rel="canonical" href={`https://client.dimo-diagnostic.net/bank-imprint${id ? `/${id}` : ''}`} />
      </Helmet>
      <Card className="mb-6 shadow-md">
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center items-center h-[calc(100vh-200px)]">
              <Spinner className="h-8 w-8" style={{ color: colors.dimoPrimary }} />
            </div>
          ) : error ? (
            <p className="text-red-500">Une erreur s'est produite lors du chargement des données. Veuillez réessayer plus tard.</p>
          ) : !selectedProperty ? ( // Si aucune propriété sélectionnée après le chargement et les filtres
            <Typography className="text-center">Il n'y a actuellement aucun dossier nécessitant une empreinte bancaire.</Typography>
          ) : (
            <>
              {propertiesNeedingImprint.length > 1 && (
                <PropertySelector
                  selectedProperty={selectedProperty.id.toString()} // Assurer que c'est une string
                  setSelectedProperty={handlePropertyChange}
                  properties={propertiesNeedingImprint}
                  isLoading={loading} // On peut le laisser car le loading global est géré
                />
              )}

              <Typography variant="h4" color="blue-gray" className="mb-5">
                Confirmation de rendez-vous par empreinte bancaire
              </Typography>
              
              <Typography variant="paragraph" color="gray">
                Pour finaliser la confirmation de votre rendez-vous, une empreinte bancaire est requise. 
                Aucun montant ne sera prélevé à ce stade. Cette étape sert uniquement de garantie.
              </Typography>

              <PropertyCard 
                property={selectedProperty} 
                variant="informative" 
                className="mb-6" 
              />

              {selectedProperty.bankImprintLink ? (
                <div className="relative w-full h-[calc(100vh-150px)]"> {/* Ajuster hauteur si nécessaire */} 
                  {iframeLoading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-gray-100 bg-opacity-75 z-10">
                      <Spinner className="h-8 w-8" style={{ color: colors.dimoPrimary }} />
                    </div>
                  )}
                  <iframe
                    src={selectedProperty.bankImprintLink}
                    title="Formulaire d'empreinte bancaire"
                    className="w-full h-full border-0 relative z-0" // z-0 pour être derrière le spinner
                    onLoad={handleIframeLoad}
                    onError={() => { /* Optionnel: gérer erreur iframe */ setIframeLoading(false); }}
                  />
                </div>
              ) : (
                 <Typography color="red">Le lien pour l'empreinte bancaire n'est pas disponible pour ce dossier. Veuillez contacter le support.</Typography>
              )}
            </>
          )}
        </div>
      </Card>
    </Layout>
  );
} 